import {useState, useCallback, useEffect, useMemo} from 'react';

const buildItemMap = (cache) => {
    const map = new Map();
    for (const children of Object.values(cache)) {
        (children.items || []).forEach((i) => map.set(i.id, i));
    }
    return map;
};

const initOverridesFromDiffs = (diffs) => {
    const map = new Map();
    if (!diffs) return map;
    if (diffs.selectAll !== undefined) {
        const entries = diffs.selectAll ? diffs.removed : diffs.added;
        if (entries) entries.forEach((id) => map.set(id, !diffs.selectAll));
        return map;
    }
    if (diffs.added) diffs.added.forEach((id) => map.set(id, true));
    if (diffs.removed) diffs.removed.forEach((id) => map.set(id, false));
    return map;
};

const resolveOverrideValue = (itemId, newValue, baseState, itemMap, role) => {
    if (baseState !== null) return newValue === baseState ? 'delete' : newValue;
    const original = !!itemMap.get(itemId)?.permittedRoles?.[role];
    return newValue === original ? 'delete' : newValue;
};

const buildBulkDiffs = (baseState, overrides) => {
    const added = new Set();
    const removed = new Set();
    overrides.forEach((val, id) => (val ? added : removed).add(id));
    return {selectAll: baseState, added, removed};
};

const buildIndividualDiffs = (overrides, itemMap, role) => {
    const added = new Set();
    const removed = new Set();
    for (const [itemId, permitted] of overrides) {
        const original = !!itemMap.get(itemId)?.permittedRoles?.[role];
        if (permitted && !original) added.add(itemId);
        else if (!permitted && original) removed.add(itemId);
    }
    return {added, removed};
};

const useRoleOverrides = (role, loadedChildren, currentDiffs, folderCounts) => {
    const [baseState, setBaseState] = useState(null);
    const [overrides, setOverrides] = useState(new Map());
    const [folderStates, setFolderStates] = useState(new Map());

    useEffect(() => {
        setBaseState(currentDiffs?.selectAll ?? null);
        setOverrides(initOverridesFromDiffs(currentDiffs));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const itemMap = useMemo(() => buildItemMap(loadedChildren), [loadedChildren]);

    const isPermitted = useCallback((itemId) => {
        if (overrides.has(itemId)) return overrides.get(itemId);
        if (baseState === true) return true;
        if (baseState === false) return false;
        return !!itemMap.get(itemId)?.permittedRoles?.[role];
    }, [overrides, baseState, itemMap, role]);

    const toggleItem = useCallback((itemId) => {
        const newValue = !isPermitted(itemId);
        setOverrides((prev) => {
            const next = new Map(prev);
            const result = resolveOverrideValue(itemId, newValue, baseState, itemMap, role);
            result === 'delete' ? next.delete(itemId) : next.set(itemId, result);
            return next;
        });
    }, [isPermitted, baseState, itemMap, role]);

    const collectDescendantItems = useCallback((folderId) => {
        const cached = loadedChildren[folderId];
        if (!cached) return [];
        const items = [...(cached.items || [])];
        (cached.folders || []).forEach((f) => items.push(...collectDescendantItems(f.id)));
        return items;
    }, [loadedChildren]);

    const toggleItems = useCallback((items, value, folderIds) => {
        setOverrides((prev) => {
            const next = new Map(prev);
            items.forEach((item) => {
                const shouldDelete = baseState !== null && value === baseState;
                shouldDelete ? next.delete(item.id) : next.set(item.id, value);
            });
            return next;
        });
        if (folderIds) {
            const ids = Array.isArray(folderIds) ? folderIds : [folderIds];
            setFolderStates((prev) => {
                const next = new Map(prev);
                ids.forEach((id) => next.set(id, value));
                return next;
            });
        }
    }, [baseState]);

    const toggleFolder = useCallback((folderId) => {
        const allItems = collectDescendantItems(folderId);
        if (!allItems.length) return;
        const newValue = !allItems.every((item) => isPermitted(item.id));
        toggleItems(allItems, newValue);
    }, [collectDescendantItems, isPermitted, toggleItems]);

    const applyAll = useCallback((permitted) => {
        setBaseState(permitted);
        setOverrides(new Map());
        setFolderStates(new Map());
    }, []);

    const buildDiffs = useCallback(
        () => baseState !== null
            ? buildBulkDiffs(baseState, overrides)
            : buildIndividualDiffs(overrides, itemMap, role),
        [baseState, overrides, itemMap, role],
    );

    const getFolderCheckState = useCallback((folderId) => {
        const allItems = collectDescendantItems(folderId);
        if (!allItems.length) {
            if (folderStates.has(folderId)) return {checked: folderStates.get(folderId), indeterminate: false};
            const fc = folderCounts?.[folderId];
            if (fc && fc.total > 0) {
                const permitted = fc.roleCounts[role] || 0;
                return {checked: permitted === fc.total, indeterminate: permitted > 0 && permitted < fc.total};
            }
            if (baseState !== null) return {checked: baseState, indeterminate: false};
            return {checked: false, indeterminate: false};
        }
        const count = allItems.filter((item) => isPermitted(item.id)).length;
        return {checked: count === allItems.length, indeterminate: count > 0 && count < allItems.length};
    }, [collectDescendantItems, isPermitted, baseState, folderStates, folderCounts, role]);

    return {isPermitted, toggleItem, toggleItems, toggleFolder, applyAll, buildDiffs, getFolderCheckState};
};

export default useRoleOverrides;
